// @ts-nocheck

// This file is automatically generated. Do not edit it directly.
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';

export const SUPABASE_URL = "https://opibkpaosvqkgtrhvklb.supabase.co";
const SUPABASE_PUBLISHABLE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im9waWJrcGFvc3Zxa2d0cmh2a2xiIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDczMjc1OTgsImV4cCI6MjA2MjkwMzU5OH0.6MKNKpbgvhur4Nap7asprQ430dScQJGgMvsdNn5dhPk";
export const STORAGE_PUBLIC_BASE = `${SUPABASE_URL}/storage/v1/object/public`;

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    storage: typeof window !== 'undefined' ? localStorage : undefined,
    persistSession: typeof window !== 'undefined',
    autoRefreshToken: true
  },
  global: {
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Accept-Profile': 'public'
    }
  },
  debug: process.env.NODE_ENV !== 'production'
});

// Type-safe handling of Supabase errors
export const handleSupabaseError = (error: any) => {
  console.error("Supabase error:", error);
  let message = "An unexpected error occurred";
  
  if (error?.message) {
    message = error.message;
  } else if (typeof error === 'string') {
    message = error;
  } else if (error?.error_description) {
    message = error.error_description;
  }
  
  // For policy violations, provide more specific guidance
  if (message.includes("new row violates row-level security") || 
      message.includes("permission denied") ||
      message.includes("violates row-level security policy")) {
    console.log("RLS policy violation detected - checking auth status");
    
    // Check if user is logged in
    const session = supabase.auth.getSession();
    console.log("Current session available:", !!session);
    
    // Provide helpful message
    if (message.includes("broadcasts")) {
      message = "You don't have permission to modify broadcasts. Please make sure you're logged in with an admin account.";
    } else if (message.includes("artworks")) {
      message = "You don't have permission to modify artworks. Please make sure you're logged in with an admin account.";
    } else if (message.includes("auctions")) {
      message = "You don't have permission to modify auctions. Please make sure you're logged in with an admin account.";
    } else {
      message = "Permission denied. This operation requires admin privileges.";
    }
  }
  
  // Add detailed logging and messages for status codes
  if (error?.status) {
    console.error("API error status:", error.status);
    console.error("API error details:", error);
    
    if (error?.status === 406) {
      message = "Server cannot produce a response matching the list of acceptable values. Check request headers.";
    } else if (error?.status === 400) {
      message = "Bad request. The request could not be understood by the server.";
    } else if (error?.status === 401 || error?.status === 403) {
      message = "Access denied. You may need to log in again or lack necessary permissions.";
    }
  }
  
  return message;
};

// Helper for safe data fetching with proper error handling and flexible typing
export const safeQuery = async <T>(
  queryBuilder: any,
  options: { single?: boolean; maybeSingle?: boolean } = {}
) => {
  try {
    let query = queryBuilder;
    
    if (options.single) {
      query = query.single();
    } else if (options.maybeSingle) {
      query = query.maybeSingle();
    }
    
    const { data, error } = await query;
    
    if (error) {
      console.error("Query error:", error);
      return { data: null, error: handleSupabaseError(error) };
    }
    
    return { data: data as T, error: null };
  } catch (error) {
    console.error("Safe query error:", error);
    return { data: null, error: handleSupabaseError(error) };
  }
};

// Helper to safely cast IDs - handles both string and UUID types
export const safeId = (id: any): string => {
  if (typeof id === 'string') return id;
  if (id && typeof id === 'object' && 'toString' in id) return id.toString();
  return String(id);
};

// Safe update helper that handles type casting
export const safelyUpdateTable = async <T extends keyof Database['public']['Tables']>(
  table: T,
  data: any, // Allow flexible typing for updates
  matchColumn: string,
  matchValue: any
) => {
  try {
    const { data: result, error } = await supabase
      .from(table)
      .update(data)
      .eq(matchColumn as any, matchValue)
      .select();

    if (error) {
      console.error(`Error updating ${table}:`, error);
      return { data: null, error: handleSupabaseError(error) };
    }

    return { data: result, error: null };
  } catch (error) {
    console.error(`Error updating ${table}:`, error);
    return { data: null, error: handleSupabaseError(error) };
  }
};

// Safe insert helper that handles type casting
export const safelyInsertIntoTable = async <T extends keyof Database['public']['Tables']>(
  table: T,
  data: any // Allow flexible typing for inserts
) => {
  try {
    const { data: result, error } = await supabase
      .from(table)
      .insert(data)
      .select();
    
    if (error) {
      console.error(`Error inserting into ${table}:`, error);
      return { data: null, error: handleSupabaseError(error) };
    }

    return { data: result, error: null };
  } catch (error) {
    console.error(`Error inserting into ${table}:`, error);
    return { data: null, error: handleSupabaseError(error) };
  }
};

// Safe delete helper
export const safelyDeleteFromTable = async <T extends keyof Database['public']['Tables']>(
  table: T,
  matchColumn: string,
  matchValue: any
) => {
  try {
    const { error } = await supabase
      .from(table)
      .delete()
      .eq(matchColumn as any, matchValue);

    if (error) {
      console.error(`Error deleting from ${table}:`, error);
      return { error: handleSupabaseError(error) };
    }

    return { error: null };
  } catch (error) {
    console.error(`Error deleting from ${table}:`, error);
    return { error: handleSupabaseError(error) };
  }
};

// Helper for handling safely fetching Supabase data
export const safelyFetchData = async <T>(queryPromise: Promise<T>) => {
  try {
    const result = await queryPromise;
    return result;
  } catch (error) {
    console.error("Error fetching data:", error);
    throw new Error(handleSupabaseError(error));
  }
};

// Helper to safely call RPC functions
export const safelyCallRPC = async <T>(
  functionName: string, 
  params: Record<string, any> = {}
) => {
  try {
    const { data, error } = await supabase.rpc(functionName, params);
    
    if (error) {
      console.error(`Error calling RPC function ${functionName}:`, error);
      return { data: null, error: handleSupabaseError(error) };
    }
    
    return { data: data as T, error: null };
  } catch (error) {
    console.error(`Error calling RPC function ${functionName}:`, error);
    return { data: null, error: handleSupabaseError(error) };
  }
};
